import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Animal, DrawStep } from "../data/animals";
import { chooseNext, chooseNextInLevels } from "../data/animals";
import { isMasterpiece } from "../data/masterpieces";
import { isFlag } from "../data/worldcup";
import { isFriendOnly, FRIEND_DRAWINGS } from "../data/friends";
import { expandColorSteps } from "../data/expandColor";
import { rewardJustUnlocked, rewardProgress } from "../data/rewards";
import { useDrawingPlayer } from "../hooks/useDrawingPlayer";
import { useKeyboard } from "../hooks/useKeyboard";
import { useWakeLock } from "../hooks/useWakeLock";
import { AnimatedDrawing } from "./AnimatedDrawing";
import { Brand } from "./Brand";
import { Controls } from "./Controls";
import { Celebration } from "./Celebration";
import { useVoiceControl } from "../voice/VoiceProvider";
import { usePlaySound } from "../sound/SoundProvider";
import { useSpeak } from "../speech/SpeakProvider";
import { useAutoPlay } from "../settings/AutoPlayProvider";
import { heardAny } from "../voice/match";
import { useLocale, useT } from "../i18n/LocaleProvider";
import {
  accusativeOf,
  genderOf,
  subjectGenderOf,
  localizeAnimal,
  localizeLevelLabel,
  rewardTextOf,
} from "../i18n/localize";
import type { Locale } from "../i18n/locale";
import type { Gender, Strings } from "../i18n/strings";

interface Props {
  animal: Animal;
  /** The sibling drawings to auto-advance among (same collection/level). */
  pool: Animal[];
  /** Drawings already finished — used to pick the next one to auto-advance to. */
  completed: Set<string>;
  onHome: () => void;
  /** Label for the back button — defaults to the home button, but paintings
   *  return to the paintings gallery, so it's labelled for that destination. */
  homeLabel?: string;
  homeAria?: string;
  /** When drawing from the sticker shelf, auto-advance cycles within `pool`
   *  (the earned stickers) rather than graduating through the levels. */
  stickerMode?: boolean;
  /** Called when the child finishes the drawing (the celebration fires). */
  onComplete: () => void;
  /** Navigate to another drawing (used by the celebration's auto-advance). */
  onGoTo: (animal: Animal) => void;
  /** "Export" look for screen-recording (e.g. YouTube): swap the app chrome for
   *  the Learn 2 Draw brand header and hide the controls, so the frame shows
   *  only the logo, the drawing and its hint. Set via the `?export=1` URL param. */
  exportMode?: boolean;
  /** Thumbnail mode: like export but hides step counter, hint, and shows
   *  finished drawing immediately. Set via `?thumb=1` URL param. */
  thumbMode?: boolean;
  /** Initial pen speed (seconds per stroke, 1 fast … 9 slow) — used by export
   *  URLs (`?speed=slowest`) so a recording is fully configured up front. */
  initialDuration?: number;
  /** Auto-start the hands-free Steady-Pace loop on mount (`?autoplay=1`). */
  autoStart?: boolean;
}

// Twinkly stars shown around the finished picture (before the celebration).
// Positions are relative to a box inset slightly outside the drawing.
const FINISH_STARS: { char: string; style: React.CSSProperties }[] = [
  { char: "✨", style: { top: "0%", left: "0%" } },
  { char: "⭐", style: { top: "-5%", left: "45%" } },
  { char: "✨", style: { top: "0%", right: "0%" } },
  { char: "⭐", style: { top: "42%", right: "-5%" } },
  { char: "✨", style: { bottom: "0%", right: "2%" } },
  { char: "🌟", style: { bottom: "-5%", left: "45%" } },
  { char: "✨", style: { bottom: "0%", left: "2%" } },
  { char: "⭐", style: { top: "42%", left: "-5%" } },
];

// The hands-free "Steady Pace" video loop is now driven by the global Auto Play
// preference (set on the home page via AutoPlayProvider) rather than an in-player
// button: when on, opening a drawing walks through its steps on its own and loops.

// How long a finished picture holds on screen before the video loops back to a
// blank canvas and draws itself again (ms).
const WATCH_LOOP_HOLD_MS = 3500;
// The long, steady pause after each step's strokes finish, before the next
// step begins (ms) — the calm beat that makes it easy to glance up and follow.
const WATCH_STEP_PAUSE_MS = 2600;

/** Estimate how long a single step should stay on screen in video mode: the
 *  time for its strokes (or colour fills) to draw at the current speed, padded
 *  so the pen almost always finishes, plus one long steady pause. */
function stepScreenMs(step: DrawStep, duration: number): number {
  if (step.strokes.length === 0) {
    // Colour-in step: each painted region fades in one at a time.
    const fillDur = Math.min(Math.max(duration * 0.3, 0.4), 1);
    const painted = (step.fills ?? []).filter((f) => !f.paper).length || 1;
    return painted * fillDur * 1000 + WATCH_STEP_PAUSE_MS;
  }
  // Count pen-down sub-paths (each absolute `M` starts one) — they draw in
  // turn, one pencil at a time. Pad by 1.8× the nominal per-stroke time so long
  // strokes (which scale up to 2.5×) still finish before we move on.
  const segs = step.strokes.reduce((n, d) => n + (d.match(/M/g)?.length ?? 1), 0);
  return Math.max(segs, 1) * duration * 1800 + WATCH_STEP_PAUSE_MS;
}

// The big heading above the canvas. Paintings show their own name ("Starry
// Night"); flags (and anything with a `title`) read "How to draw a French
// flag" with the right a/an; everything else is "How to draw a <name>".
function drawingTitle(animal: Animal, t: Strings, gender: Gender, locale: Locale): string {
  if (animal.artist) return `${animal.emoji} ${animal.name}`;
  // Greek returns the accusative-with-article ("έναν σκύλο"); en/fr return the
  // nominative subject unchanged, so their heading is byte-for-byte as before.
  const subject = accusativeOf(animal, locale);
  return `${animal.emoji} ${t.howToDraw(subject, gender)}`;
}

export function DrawingPlayer({
  animal,
  pool,
  completed,
  onHome,
  homeLabel,
  homeAria,
  stickerMode = false,
  onComplete,
  onGoTo,
  exportMode = false,
  thumbMode = false,
  initialDuration,
  autoStart = false,
}: Props) {
  const playSound = usePlaySound();
  // The play-button progress ring: the drawing reports its live 0..1 progress
  // each frame and we drive the arc's dash-offset imperatively — no per-frame
  // React re-render. The arc uses pathLength=1, so offset = 1 - progress.
  const ringRef = useRef<SVGCircleElement | null>(null);
  const handleProgress = useCallback((progress: number) => {
    const arc = ringRef.current;
    if (arc) arc.style.strokeDashoffset = String(1 - Math.max(0, Math.min(1, progress)));
  }, []);
  const { speak, cancel: cancelSpeech, isSpeaking } = useSpeak();
  // Expand the single "color it in" step into one step per colour, so colouring
  // is paced one crayon at a time (memoised — it measures geometry via the DOM).
  const { locale } = useLocale();
  const t = useT();
  const gender = genderOf(animal.id, locale);
  const titleGender = subjectGenderOf(animal.id, locale);
  const drawAnimal = useMemo(
    () => expandColorSteps(animal, t.nextColour),
    [animal, t.nextColour],
  );
  const player = useDrawingPlayer(drawAnimal.steps.length);
  // After the last step, the picture first "finishes" (shown static with stars
  // around it); pressing next once more pops the celebration.
  const [finished, setFinished] = useState(false);
  const [celebrating, setCelebrating] = useState(false);

  // Thumbnail mode: skip to finished state immediately
  useEffect(() => {
    if (thumbMode) {
      player.setStepIndex(drawAnimal.steps.length - 1);
      setFinished(true);
    }
  }, [thumbMode, drawAnimal.steps.length, player]);

  // "Steady Pace" hands-free video mode: the drawing auto-advances through its
  // steps with long pauses and loops on this same picture (no celebration or
  // graduation to another drawing), so a toddler can glance up and follow along.
  const [watching, setWatching] = useState(false);
  // Global "Auto Play" preference (toggled on the home page). When on, the
  // hands-free loop starts automatically on mount (see the bootstrap effect).
  const { enabled: autoPlayEnabled } = useAutoPlay();

  // Decided once when the celebration appears, so the countdown target is stable.
  // Paintings cycle among themselves; the leveled drawings stay within their
  // level until it's cleared, then graduate to the next level.
  const nextUp = useMemo<{
    animal: Animal | null;
    levelUp?: { from: string; to: string };
  } | null>(() => {
    if (!celebrating) return null;
    // Stickers, paintings, flags and reward "friends" cycle within their own
    // pool; the leveled drawings graduate level by level.
    if (
      stickerMode ||
      isMasterpiece(animal.id) ||
      isFlag(animal.id) ||
      isFriendOnly(animal.id)
    ) {
      // The earned-sticker pool can be empty (e.g. a friend opened by deep link
      // before any sticker is unlocked) — guard so we don't index an empty pool.
      const next = pool.length ? chooseNext(pool, animal.id, completed) : null;
      // chooseNext returns the same drawing when there's no other to offer
      // (e.g. only one earned sticker) — treat that as "no next".
      return { animal: !next || next.id === animal.id ? null : next };
    }
    // `pool` arrives localized, but chooseNextInLevels reaches into the drawing
    // data itself (it may graduate to a HARDER level, which isn't in `pool`), so
    // its pick comes back in English — localize it or the celebration offers
    // "Ensuite : Hedgehog".
    const up = chooseNextInLevels(animal, completed);
    return { ...up, animal: up.animal ? localizeAnimal(up.animal, locale) : null };
  }, [celebrating, pool, animal, completed, stickerMode, locale]);

  // Whether this drawing was already finished when we opened it. Captured once
  // at mount (the player remounts per drawing via its key), so replaying an
  // already-earned sticker doesn't re-trigger the milestone reward.
  const wasDoneBefore = useRef(completed.has(animal.id));

  // If *newly* finishing this drawing landed on a 5-drawing milestone, surface
  // the earned animal friend right here in the celebration — no trip to the
  // sticker shelf needed. `completed` already includes the drawing just done.
  // Skipped on replays, so it only fires the moment a milestone is crossed.
  const reward = useMemo(() => {
    if (celebrating && !wasDoneBefore.current && !isFriendOnly(animal.id)) {
      // Count only reward-eligible drawings — the same tally the sticker shelf
      // shows — so a friend earned here is already unlocked there (not a step
      // behind). `completed` already includes the drawing just finished.
      const r = rewardJustUnlocked(rewardProgress(completed));
      // The reward's English name is a KEY (FRIEND_DRAWINGS); translate it only
      // for display, and carry the article the sentence needs ("l'Éléphant"),
      // while keeping the raw drawing so the child can tap to draw it now.
      if (r) return { ...r, ...rewardTextOf(r.name, locale), drawing: FRIEND_DRAWINGS[r.name] };
    }
    return undefined;
  }, [celebrating, completed, animal.id, locale]);

  // Last step → first press shows the finished picture with stars (no pop-up);
  // a second press pops the celebration. Every transition is one deliberate
  // press, so finishing by accident is easy to undo.
  const handleNext = useCallback(() => {
    if (celebrating) return;
    if (finished) {
      setCelebrating(true);
      playSound("celebrate");
    } else if (player.isLast) {
      setFinished(true);
      playSound("finish");
      onComplete();
    } else {
      // Moving to a new step always plays it: if the child had paused the
      // drawing mid-stroke, stepping resumes the animation so the next step
      // draws itself rather than sitting frozen.
      player.setPaused(false);
      player.next();
      playSound("step");
    }
  }, [celebrating, finished, player, onComplete, playSound]);

  // Back steps out one stage at a time: celebration → finished → last step,
  // so a mistaken press is fully recoverable.
  const handlePrev = useCallback(() => {
    if (celebrating) setCelebrating(false);
    else if (finished) setFinished(false);
    else {
      // Same as stepping forward: a paused drawing resumes so the step we land
      // on animates itself.
      player.setPaused(false);
      player.prev();
    }
  }, [celebrating, finished, player]);

  useKeyboard({ onNext: handleNext, onPrev: handlePrev, onHome });

  const step = drawAnimal.steps[player.stepIndex];

  // "Still picture" mode shows each step already drawn (no self-drawing motion).
  // Never in export/recording mode — a video always animates.
  const stillActive = !exportMode && player.still;

  // Bootstrap on mount: apply any URL-configured speed and start the hands-free
  // loop from a clean blank canvas when it's requested — either by the global
  // Auto Play preference (home page) or the export URL `?autoplay=1`
  // (so `?export=1&speed=slowest&autoplay=1` is a self-contained recording).
  const didInitExport = useRef(false);
  useEffect(() => {
    if (didInitExport.current) return;
    didInitExport.current = true;
    if (typeof initialDuration === "number") player.setDuration(initialDuration);
    if (autoStart || autoPlayEnabled) {
      setCelebrating(false);
      setFinished(false);
      player.setPaused(false);
      player.reset();
      setWatching(true);
    }
    // Run once on mount; player identity is stable enough for this bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the latest onComplete/playSound from the timer without making them
  // effect deps — onComplete is an inline arrow (new identity every render), so
  // depending on it would restart the running beat on any unrelated re-render.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const playSoundRef = useRef(playSound);
  playSoundRef.current = playSound;

  // The video engine: hold each step on screen for its estimated draw time plus
  // a long steady pause, then advance. On the final step it shows the finished
  // picture with stars, holds a beat, then loops back to the start — forever,
  // until the child (or a grown-up) stops it. A single timer keyed on the step
  // index, so manual nav or a speed change simply restarts the current beat.
  useEffect(() => {
    if (!watching || celebrating) return;
    if (finished) {
      const t = window.setTimeout(() => {
        setFinished(false);
        player.reset();
      }, WATCH_LOOP_HOLD_MS);
      return () => window.clearTimeout(t);
    }
    let cancelled = false;
    let pollId: ReturnType<typeof setTimeout> | undefined;
    const advance = () => {
      if (cancelled) return;
      if (player.isLast) {
        setFinished(true);
        onCompleteRef.current();
        playSoundRef.current("finish");
      } else {
        player.next();
        playSoundRef.current("step");
      }
    };
    // Safety net: once the drawing has had its time, don't advance while the
    // read-aloud voice is still talking — otherwise a step whose strokes/fills
    // draw faster than their narration (e.g. a quick colour-in sub-step) would
    // cut its own voice off and overlap the next step. So a step lasts at least
    // as long as BOTH its drawing and its narration. Capped so a stuck clip
    // can't freeze the loop.
    const waitStart = Date.now();
    const afterDraw = () => {
      if (cancelled) return;
      if (isSpeaking() && Date.now() - waitStart < 12000) {
        pollId = setTimeout(afterDraw, 150);
      } else {
        advance();
      }
    };
    const t = window.setTimeout(afterDraw, stepScreenMs(step, player.duration));
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (pollId) clearTimeout(pollId);
    };
  }, [
    watching,
    celebrating,
    finished,
    step,
    player.stepIndex,
    player.isLast,
    player.duration,
    player.next,
    player.reset,
    isSpeaking,
  ]);

  // Keep the screen awake while watching a drawing animate (like a video app),
  // so the laptop doesn't dim or sleep during hands-free viewing.
  useWakeLock(true);

  // Voice commands on the drawing page are kept to just three simple words:
  // "next", "back" and "home". Check "back" before "next" so "go back" isn't
  // misread as "go" → next.
  const onCommand = useCallback(
    (transcript: string): boolean => {
      if (heardAny(transcript, t.commands.home)) {
        onHome();
        return true;
      }
      if (heardAny(transcript, t.commands.back)) {
        handlePrev();
        return true;
      }
      if (heardAny(transcript, t.commands.next)) {
        handleNext();
        return true;
      }
      return false;
    },
    [onHome, handlePrev, handleNext, t],
  );
  useVoiceControl(onCommand);

  // The instruction describing the current action — shown in the controls,
  // between the step nav and the pause button.
  const hint = celebrating
    ? ""
    : finished
      ? exportMode
        ? t.allDoneExport
        : watching
          ? t.allDoneWatchAgain
          : t.allDoneSurprise
      : step.hint;

  // Read the current instruction aloud whenever it changes, so a pre-reader
  // hears what to do (e.g. "This is a CIRCLE! Draw one big round circle").
  // `speak` no-ops when read-aloud is switched off; it also cancels any
  // half-spoken previous hint. Stop talking when we leave the drawing.
  useEffect(() => {
    if (hint) speak(hint, animal.id);
    return () => cancelSpeech();
  }, [hint, speak, cancelSpeech, animal.id]);

  useEffect(() => {
    document.title = `${drawingTitle(animal, t, titleGender, locale)} — Learn 2 Draw`;
    return () => {
      document.title = "Learn 2 Draw — Free Drawing App & Website for Little Kids & Toddlers, Step by Step 🎨";
    };
  }, [animal, t, titleGender, locale]);

  return (
    <div className={thumbMode ? "player player-thumb" : exportMode ? "player player-export" : "player"}>
      {exportMode || thumbMode ? (
        // Recording look: just the Learn 2 Draw brand header — no app chrome.
        <Brand />
      ) : (
        <>
          <header className="player-header">
            <button className="home-btn" onClick={onHome} aria-label={homeAria ?? t.backToHome}>
              {homeLabel ?? t.home}
            </button>
            <h1>{drawingTitle(animal, t, titleGender, locale)}</h1>
            {animal.artist && (
              <span className="artist-line">
                {t.by} {animal.artist}
              </span>
            )}
          </header>
        </>
      )}

      <div className="art-stage">
        {animal.image ? (
          <div className="art-compare">
            <figure className="art-pane">
              <AnimatedDrawing
                animal={drawAnimal}
                stepIndex={player.stepIndex}
                duration={player.duration}
                frozen={finished || celebrating}
                paused={player.paused}
                still={stillActive}
                showGrid={!exportMode}
                onProgress={handleProgress}
              />
              <figcaption className="art-label">{t.yourSketch}</figcaption>
            </figure>
            <figure className="art-pane">
              <img
                className="original-art"
                src={animal.image}
                alt={`${animal.name} by ${animal.artist}`}
                loading="lazy"
                decoding="async"
              />
              <figcaption className="art-label">{t.realPainting}</figcaption>
            </figure>
          </div>
        ) : (
          <AnimatedDrawing
            animal={drawAnimal}
            stepIndex={player.stepIndex}
            duration={player.duration}
            frozen={finished || celebrating}
            paused={player.paused}
            still={stillActive}
            showGrid={!exportMode}
            onProgress={handleProgress}
          />
        )}

        {finished && !thumbMode && (
          <div className="finish-stars" aria-hidden="true">
            {FINISH_STARS.map((s, i) => (
              <span
                key={i}
                className="finish-star"
                style={{ ...s.style, animationDelay: `${i * 0.15}s` }}
              >
                {s.char}
              </span>
            ))}
          </div>
        )}
      </div>

      {thumbMode && (
        <img
          className="thumb-mascot"
          src="/crayon-mascot.png"
          alt=""
          aria-hidden="true"
          draggable={false}
        />
      )}

      {!thumbMode && <p className="hint">{hint}</p>}

      {/* Export/recording look keeps the step counter (but nothing else) below
          the drawing, so a viewer can see progress. Hidden in thumb mode. */}
      {exportMode && !celebrating && !thumbMode && (
        <p className="step-indicator export-step" aria-live="polite">
          {t.stepOf(
            Math.min(player.stepIndex + 1, drawAnimal.steps.length),
            drawAnimal.steps.length,
          )}
        </p>
      )}

      {!exportMode && !thumbMode && (
      <Controls
        stepIndex={player.stepIndex}
        stepCount={drawAnimal.steps.length}
        prevDisabled={player.isFirst && !finished && !celebrating}
        nextDisabled={celebrating}
        duration={player.duration}
        paused={player.paused}
        pauseDisabled={finished || celebrating || watching || player.still}
        still={player.still}
        onPrev={handlePrev}
        onNext={handleNext}
        onSpeedChange={player.setDuration}
        onTogglePause={player.togglePause}
        onToggleStill={player.toggleStill}
        progressRef={ringRef}
      />
      )}

      {!exportMode && celebrating && nextUp && (
        <Celebration
          animalName={animal.name}
          animalGender={gender}
          fact={animal.fact}
          reward={reward}
          onDrawReward={
            reward?.drawing ? () => onGoTo(reward.drawing as Animal) : undefined
          }
          levelUp={
            nextUp.levelUp && {
              from: localizeLevelLabel(nextUp.levelUp.from, t),
              to: localizeLevelLabel(nextUp.levelUp.to, t),
            }
          }
          nextName={nextUp.animal?.name}
          nextEmoji={nextUp.animal?.emoji}
          onAutoNext={
            nextUp.animal ? () => onGoTo(nextUp.animal as Animal) : undefined
          }
          onAgain={onHome}
          onWatchAgain={() => {
            setCelebrating(false);
            setFinished(false);
            player.reset();
          }}
          onBack={handlePrev}
        />
      )}
    </div>
  );
}
